import { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Typography,
  Container,
  Box,
  Paper,
  FormControl,
  Card,
  Divider,
  CircularProgress,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
//import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import dayjs from "dayjs";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import CustomTabs from "../../common/custom-tabs";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import AppTable from "../../common/app-table";
import AppDropdown from "../../common/app-dropdown";
import AppDatePicker from "../../common/app-date-picker";
import { useAuth } from "../../hooks/AuthProvider";
import {
  CHAT_ANALYTICS_ENDPOINT,
  CHAT_ANALYTICS_EXPORT_ENDPOINT,
} from "../../config/urls";

// Formats table dateTime strings ("YYYY-MM-DD" or "YYYY-MM-DD HH:mm")
// to DD/MM/YYYY for consistency across the page
const formatTableDateTime = (value) => {
  const parsed = dayjs(value);
  if (!parsed.isValid()) return value;
  const hasTime = /\d{2}:\d{2}/.test(String(value));
  return parsed.format(hasTime ? "DD/MM/YYYY HH:mm" : "DD/MM/YYYY");
};

const initialAgentActivityData = [
  {
    name: "ADMIN",
    currentStatus: "Online",
    chatHandled: 0,
    onlineDuration: "2 hr 13 min 5 sec",
    offlineDuration: "13 min 5 sec",
    firstOnline: "19-12-2025 05:34 PM",
    lastOnline: "01-02-2026 01:14 PM",
    timesOnline: 11,
    avgOnlineDuration: "14 min 25 sec",
  },
  {
    name: "Agent 1",
    currentStatus: "Online",
    chatHandled: 34,
    onlineDuration: "2 hr 13 min 5 sec",
    offlineDuration: "13 min 5 sec",
    firstOnline: "19-12-2025 05:34 PM",
    lastOnline: "01-02-2026 01:14 PM",
    timesOnline: 67,
    avgOnlineDuration: "14 min 25 sec",
  },
  {
    name: "Agent 2",
    currentStatus: "Offline",
    chatHandled: 12,
    onlineDuration: "2 hr 13 min 5 sec",
    offlineDuration: "13 min 5 sec",
    firstOnline: "19-12-2025 05:34 PM",
    lastOnline: "01-02-2026 01:14 PM",
    timesOnline: 32,
    avgOnlineDuration: "14 min 25 sec",
  },
  {
    name: "Agent 3",
    currentStatus: "Online",
    chatHandled: 24,
    onlineDuration: "2 hr 13 min 5 sec",
    offlineDuration: "13 min 5 sec",
    firstOnline: "19-12-2025 05:34 PM",
    lastOnline: "01-02-2026 01:14 PM",
    timesOnline: 54,
    avgOnlineDuration: "14 min 25 sec",
  },
  {
    name: "Agent 4",
    currentStatus: "Offline",
    chatHandled: 8,
    onlineDuration: "1 hr 45 min 20 sec",
    offlineDuration: "25 min 10 sec",
    firstOnline: "20-12-2025 09:15 AM",
    lastOnline: "31-01-2026 06:30 PM",
    timesOnline: 23,
    avgOnlineDuration: "12 min 30 sec",
  },
  {
    name: "Agent 5",
    currentStatus: "Online",
    chatHandled: 42,
    onlineDuration: "3 hr 20 min 15 sec",
    offlineDuration: "18 min 45 sec",
    firstOnline: "18-12-2025 08:00 AM",
    lastOnline: "02-02-2026 11:45 AM",
    timesOnline: 89,
    avgOnlineDuration: "16 min 50 sec",
  },
];

const initialChatTableData = [
  {
    dateTime: "2026-01-24 10:00",
    attendedChats: 42,
    ongoingChats: 3,
    closedChats: 39,
    missedChats: 5,
    avgPickupInterval: "18 sec",
    avgTimeTaken: "6 min 24 sec",
    avgPickupFRDuration: "12 sec",
    avgStartFRDuration: "9 sec",
    avgMissedFRDuration: "35 sec",
    avgMissedChatPickupInterval: "42 sec",
  },
  {
    dateTime: "2026-01-24 11:00",
    attendedChats: 38,
    ongoingChats: 2,
    closedChats: 36,
    missedChats: 3,
    avgPickupInterval: "15 sec",
    avgTimeTaken: "5 min 30 sec",
    avgPickupFRDuration: "10 sec",
    avgStartFRDuration: "8 sec",
    avgMissedFRDuration: "30 sec",
    avgMissedChatPickupInterval: "38 sec",
  },
  {
    dateTime: "2026-01-24 12:00",
    attendedChats: 45,
    ongoingChats: 4,
    closedChats: 41,
    missedChats: 6,
    avgPickupInterval: "20 sec",
    avgTimeTaken: "7 min 15 sec",
    avgPickupFRDuration: "14 sec",
    avgStartFRDuration: "11 sec",
    avgMissedFRDuration: "40 sec",
    avgMissedChatPickupInterval: "45 sec",
  },
  {
    dateTime: "2026-01-24 13:00",
    attendedChats: 52,
    ongoingChats: 5,
    closedChats: 47,
    missedChats: 7,
    avgPickupInterval: "22 sec",
    avgTimeTaken: "8 min 10 sec",
    avgPickupFRDuration: "16 sec",
    avgStartFRDuration: "13 sec",
    avgMissedFRDuration: "42 sec",
    avgMissedChatPickupInterval: "48 sec",
  },
  {
    dateTime: "2026-01-24 14:00",
    attendedChats: 41,
    ongoingChats: 3,
    closedChats: 38,
    missedChats: 4,
    avgPickupInterval: "17 sec",
    avgTimeTaken: "6 min 5 sec",
    avgPickupFRDuration: "11 sec",
    avgStartFRDuration: "9 sec",
    avgMissedFRDuration: "33 sec",
    avgMissedChatPickupInterval: "40 sec",
  },
];

const initialStatsData = {
  attendedChats: 459,
  missedChats: 45,
  avgPickupInterval: 18,
  avgRating: 4.3,
  avgStartFRDuration: 9,
  avgPickupFRDuration: 12,
  avgMissedFRDuration: 41,
  avgMissedChatPickupInterval: 49,
};

const ChatAnalytics = () => {
  const { getValidToken } = useAuth();

  const [department, setDepartment] = useState("sales");
  const [chatPickedFrom, setChatPickedFrom] = useState("combined");
  const [startDate, setStartDate] = useState(() =>
    dayjs().subtract(6, "day").startOf("day")
  );
  const [endDate, setEndDate] = useState(() => dayjs().endOf("day"));
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const [isLoading, setIsLoading] = useState(false);
  const [exportState, setExportState] = useState("default");
  const [dateError, setDateError] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(initialChatTableData);
  const [agentData, setAgentData] = useState(initialAgentActivityData);
  const [statsData, setStatsData] = useState(initialStatsData);

  const departments = [
    { value: "sales", label: "Sales" },
    { value: "support", label: "Support" },
    { value: "billing", label: "Billing" },
    { value: "technical", label: "Technical" },
  ];

  const chatPickedOptions = [
    { value: "combined", label: "Combined" },
    { value: "agent", label: "Agent" },
    { value: "bot", label: "Bot" },
  ];

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleSubmit = useCallback(
    async (skipValidation = false) => {
      if (
        !skipValidation &&
        startDate &&
        endDate &&
        dayjs(startDate).isAfter(dayjs(endDate))
      ) {
        setDateError("Start date must be before or equal to end date");
        return;
      }
      setDateError(null);
      setIsLoading(true);

      try {
        const token = await getValidToken();
        const tenant = localStorage.getItem("tenant");

        const queryParams = new URLSearchParams({
          department,
          chat_picked_from: chatPickedFrom,
          start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
          end_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
          tab: activeTab,
        });

        const response = await fetch(
          `${CHAT_ANALYTICS_ENDPOINT}?${queryParams}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              "X-Tenant-Id": tenant,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch analytics data");

        const data = await response.json();

        if (data.table_data) setAnalyticsData(data.table_data);
        if (data.agent_data) setAgentData(data.agent_data);
        if (data.stats) setStatsData(data.stats);
      } catch (error) {
        console.error("Error fetching analytics:", error.message);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [department, chatPickedFrom, startDate, endDate, activeTab]
  );

  useEffect(() => {
    handleSubmit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async () => {
    if (exportState !== "default") return;
    setExportState("loading");

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const queryParams = new URLSearchParams({
        department,
        chat_picked_from: chatPickedFrom,
        start_date: startDate ? dayjs(startDate).format("YYYY-MM-DD") : "",
        end_date: endDate ? dayjs(endDate).format("YYYY-MM-DD") : "",
        tab: activeTab,
      });

      const response = await fetch(
        `${CHAT_ANALYTICS_EXPORT_ENDPOINT}?${queryParams}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Tenant-Id": tenant,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to export data");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      a.download = `chat-analytics-${dateStr}.csv`;
      document.body.appendChild(a);
      a.click();
      setExportState("success");
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      setTimeout(() => setExportState("default"), 3000);
    } catch (error) {
      console.error("Error exporting data:", error.message);
      setExportState("failed");
      setTimeout(() => setExportState("default"), 3000);
    }
  };

  const cardStyle = {
    borderRadius: "12px",
    p: 2.5,
    pb: "52px",
    backgroundColor: "#FFFFFF",
    height: "100%",
  };

  const labelStyle = {
    ...typography.bodySmall,
    color: "#6C7C93",
    mb: 1.2,
  };

  const valueStyle = {
    ...typography.statLarge,
    fontSize: { xs: 32, sm: 36, md: 42 },
  };

  const getAgentActivityPageData = () => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return agentData.slice(start, end);
  };

  const agentActivityColumns = [
    {
      id: "name",
      label: "Name",
      minWidth: 100,
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {row.name}
        </span>
      ),
    },
    {
      id: "currentStatus",
      label: "Current Status",
      render: (row) => (
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            color: row.currentStatus === "Online" ? "#059669" : "#E74C3C",
            ...typography.bodySmall,
            fontWeight: 500,
          }}
        >
          {row.currentStatus}
        </Box>
      ),
    },
    {
      id: "chatHandled",
      label: "Chat Handled",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {row.chatHandled}
        </span>
      ),
    },
    {
      id: "onlineDuration",
      label: "Online Duration",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {row.onlineDuration}
        </span>
      ),
    },
    {
      id: "offlineDuration",
      label: "Offline Duration",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {row.offlineDuration}
        </span>
      ),
    },
    {
      id: "firstOnline",
      label: "First Online",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {formatTableDateTime(row.firstOnline)}
        </span>
      ),
    },
    {
      id: "lastOnline",
      label: "Last Online",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {formatTableDateTime(row.lastOnline)}
        </span>
      ),
    },
    {
      id: "timesOnline",
      label: "No of times Online",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {row.timesOnline}
        </span>
      ),
    },
    {
      id: "avgOnlineDuration",
      label: "Average Online Duration",
      render: (row) => (
        <span style={{ color: "#353535", ...typography.bodyLarge }}>
          {row.avgOnlineDuration}
        </span>
      ),
    },
    {
      id: "__break_report__",
      label: "View Break Report",
      render: () => (
        <ButtonOne
          variant="contained"
          sx={{
            backgroundColor: ColorsConstants.primaryPurple100,
            whiteSpace: "nowrap",
            minWidth: "120px",
            px: 2,
            ...typography.bodyLarge,
            "&:hover": {
              backgroundColor: ColorsConstants.primaryPurple100,
              opacity: 0.9,
            },
          }}
        >
          Break Report
        </ButtonOne>
      ),
    },
  ];

  const chatAnalyticsColumns = [
    {
      id: "dateTime",
      label: "Date & Time",
      minWidth: 180,
      render: (row) => (
        <span
          style={{
            color: "#1B2232",
            whiteSpace: "nowrap",
            ...typography.bodyLarge,
          }}
        >
          {formatTableDateTime(row.dateTime)}
        </span>
      ),
    },
    {
      id: "attendedChats",
      label: "Attended Chats",
      render: (row) => (
        <span
          style={{
            color: ColorsConstants.primaryBackgroundColor,
            fontWeight: 600,
            ...typography.bodyLarge,
          }}
        >
          {row.attendedChats}
        </span>
      ),
    },
    {
      id: "ongoingChats",
      label: "Ongoing Chats",
      render: (row) => (
        <span
          style={{ color: "#353535", fontWeight: 600, ...typography.bodyLarge }}
        >
          {row.ongoingChats}
        </span>
      ),
    },
    {
      id: "closedChats",
      label: "Closed Chats",
      render: (row) => (
        <span
          style={{ color: "#059669", fontWeight: 600, ...typography.bodyLarge }}
        >
          {row.closedChats}
        </span>
      ),
    },
    {
      id: "missedChats",
      label: "Missed Chats",
      render: (row) => (
        <span
          style={{ color: "#F14145", fontWeight: 600, ...typography.bodyLarge }}
        >
          {row.missedChats}
        </span>
      ),
    },
    {
      id: "avgPickupInterval",
      label: "Avg Pickup Interval",
      render: (row) => (
        <span style={{ color: "#1B2232", ...typography.bodyLarge }}>
          {row.avgPickupInterval}
        </span>
      ),
    },
    {
      id: "avgTimeTaken",
      label: "Avg Time Taken",
      render: (row) => (
        <span style={{ color: "#1B2232", ...typography.bodyLarge }}>
          {row.avgTimeTaken}
        </span>
      ),
    },
    {
      id: "avgPickupFRDuration",
      label: "Avg Pickup FR Duration",
      render: (row) => (
        <span style={{ color: "#1B2232", ...typography.bodyLarge }}>
          {row.avgPickupFRDuration}
        </span>
      ),
    },
    {
      id: "avgStartFRDuration",
      label: "Avg Start FR Duration",
      render: (row) => (
        <span style={{ color: "#1B2232", ...typography.bodyLarge }}>
          {row.avgStartFRDuration}
        </span>
      ),
    },
    {
      id: "avgMissedFRDuration",
      label: "Avg Missed FR Duration",
      render: (row) => (
        <span style={{ color: "#1B2232", ...typography.bodyLarge }}>
          {row.avgMissedFRDuration}
        </span>
      ),
    },
    {
      id: "avgMissedChatPickupInterval",
      label: "Avg Missed Chat Pickup Interval",
      render: (row) => (
        <span style={{ color: "#1B2232", ...typography.bodyLarge }}>
          {row.avgMissedChatPickupInterval}
        </span>
      ),
    },
  ];

  const analyticsRowSx = (row, index) => ({
    backgroundColor: index % 2 === 0 ? "#FAFAFA" : "#F0F5FF",
    "&:hover": { backgroundColor: index % 2 === 0 ? "#F5F5F5" : "#E6F0FF" },
  });

  const exportButtonLabel = {
    default: "Export",
    loading: "Exporting...",
    success: "Exported",
    failed: "Export Failed",
  };

  return (
    <Container maxWidth={false} sx={{ bgcolor: "#FAFAFA", py: 3 }}>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #DFE5EC",
          borderRadius: "12px",
          mb: 3,
          overflow: "hidden",
        }}
      >
        {/* Tabs Section */}
        <Box sx={{ p: { xs: 2, sm: 3 }, pb: 0 }}>
          <CustomTabs
            value={activeTab}
            onChange={handleTabChange}
            tabs={[
              { label: "Department", value: 0 },
              { label: "Agent", value: 1 },
              { label: "Customer", value: 2 },
              { label: "Agent Activity", value: 3 },
            ]}
          />
        </Box>

        {/* Divider */}
        <Divider sx={{ borderColor: "#DFE5EC", my: 0 }} />

        {/* Filters Section */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Grid container spacing={2} alignItems="flex-end" wrap="wrap">
            {/* Department */}
            <Grid item xs={12} sm={6} md={2} lg={3}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: "#6C7C93",
                    mb: 0.5,
                    ml: 0.5,
                  }}
                >
                  Select department type
                </Typography>
                <AppDropdown
                  menuItems={departments}
                  selectedValue={department}
                  onMenuSelect={(value) => setDepartment(value)}
                  variant="outlined"
                  endIcon={<ExpandMoreIcon />}
                >
                  <span style={{ ...typography.bodyLarge }}>
                    {departments.find((d) => d.value === department)?.label ||
                      "Select department type"}
                  </span>
                </AppDropdown>
              </FormControl>
            </Grid>

            {/* Chat Picked From */}
            <Grid item xs={12} sm={6} md={2} lg={2}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: "#6C7C93",
                    mb: 0.5,
                    ml: 0.5,
                  }}
                >
                  Chat Picked From
                </Typography>
                <AppDropdown
                  menuItems={chatPickedOptions}
                  selectedValue={chatPickedFrom}
                  onMenuSelect={(value) => setChatPickedFrom(value)}
                  variant="outlined"
                  endIcon={<ExpandMoreIcon />}
                >
                  <span style={{ ...typography.bodyLarge }}>
                    {chatPickedOptions.find((o) => o.value === chatPickedFrom)
                      ?.label || "Chat Picked From"}
                  </span>
                </AppDropdown>
              </FormControl>
            </Grid>

            {/* Start Date */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: "#6C7C93",
                    mb: 0.5,
                    ml: 0.5,
                  }}
                >
                  From Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    value={startDate}
                    onChange={(val) => {
                      setStartDate(val);
                      setDateError(null);
                    }}
                    minDate={null}
                    disableFuture={false}
                    placeholder="From Date"
                    fullWidth
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            {/* End Date */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <FormControl fullWidth>
                <Typography
                  sx={{
                    ...typography.bodySmall,
                    color: "#6C7C93",
                    mb: 0.5,
                    ml: 0.5,
                  }}
                >
                  To Date
                </Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <AppDatePicker
                    value={endDate}
                    onChange={(val) => {
                      setEndDate(val);
                      setDateError(null);
                    }}
                    minDate={null}
                    disableFuture={false}
                    placeholder="To Date"
                    fullWidth
                    error={!!dateError}
                    helperText={dateError}
                  />
                </LocalizationProvider>
              </FormControl>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} lg="auto">
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  justifyContent: {
                    xs: "flex-start",
                    lg: "flex-end",
                  },
                  width: "100%",
                  mt: { xs: 1, lg: 0 },
                }}
              >
                <ButtonOne
                  variant="contained"
                  disabled={isLoading}
                  onClick={handleSubmit}
                  startIcon={
                    isLoading ? (
                      <CircularProgress
                        size={16}
                        sx={{ color: "white" }}
                      />
                    ) : null
                  }
                  sx={{
                    backgroundColor: ColorsConstants.primaryPurple100,
                    minWidth: "120px",
                    height: "38px",
                    ...typography.bodyLarge,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                    "&:hover": {
                      backgroundColor: ColorsConstants.primaryPurple100,
                      opacity: 0.9,
                    },
                  }}
                >
                  {isLoading ? "Loading..." : "Submit"}
                </ButtonOne>

                <ButtonTwo
                  variant="outlined"
                  disabled={
                    exportState === "loading" || exportState === "success"
                  }
                  onClick={handleExport}
                  startIcon={
                    exportState === "loading" ? (
                      <CircularProgress size={16} />
                    ) : null
                  }
                  sx={{
                    minWidth: "120px",
                    height: "38px",
                    ...typography.bodyLarge,
                    fontWeight: 500,
                    whiteSpace: "nowrap",
                  }}
                >
                  {exportButtonLabel[exportState]}
                </ButtonTwo>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* Stats Cards Section - Only show for non-Agent Activity tabs */}
      {activeTab !== 3 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Row 1 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Attended Chats</Typography>
              <Typography sx={{ ...valueStyle, color: "#059669DE" }}>
                {statsData.attendedChats}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Missed Chats</Typography>
              <Typography sx={{ ...valueStyle, color: "#DC2626" }}>
                {statsData.missedChats}
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Avg Picked Up Interval</Typography>
              <Typography sx={{ ...valueStyle, color: "#EA580C" }}>
                {statsData.avgPickupInterval}{" "}
                <span
                  style={{
                    fontSize: 14,
                    color: "#000000DE",
                    ...typography.bodyLarge,
                  }}
                >
                  sec
                </span>
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Average Rating</Typography>
              <Typography sx={{ ...valueStyle, color: "#2563EB" }}>
                {statsData.avgRating}
              </Typography>
            </Card>
          </Grid>

          {/* Row 2 */}
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Avg Start FR Duration</Typography>
              <Typography sx={{ ...valueStyle, color: "#2563EB" }}>
                {statsData.avgStartFRDuration}{" "}
                <span
                  style={{
                    fontSize: 14,
                    color: "#000000DE",
                    ...typography.bodyLarge,
                  }}
                >
                  sec
                </span>
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Avg Pickup FR Duration</Typography>
              <Typography sx={{ ...valueStyle, color: "#2563EB" }}>
                {statsData.avgPickupFRDuration}{" "}
                <span
                  style={{
                    fontSize: 14,
                    color: "#000000DE",
                    ...typography.bodyLarge,
                  }}
                >
                  sec
                </span>
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Avg Missed FR Duration</Typography>
              <Typography sx={{ ...valueStyle, color: "#DC2626" }}>
                {statsData.avgMissedFRDuration}{" "}
                <span
                  style={{
                    fontSize: 14,
                    color: "#000000DE",
                    ...typography.bodyLarge,
                  }}
                >
                  sec
                </span>
              </Typography>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={cardStyle}>
              <Typography sx={labelStyle}>Avg Missed Chat Pickup L.</Typography>
              <Typography sx={{ ...valueStyle, color: "#DC2626" }}>
                {statsData.avgMissedChatPickupInterval}{" "}
                <span
                  style={{
                    fontSize: 14,
                    color: "#000000DE",
                    ...typography.bodyLarge,
                  }}
                >
                  sec
                </span>
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Report Range Section */}
      <Typography
        sx={{
          ...typography.bodySmall,
          color: "#6C7C93",
          mb: 1.5,
        }}
      >
        Report Range:{" "}
        <span style={{ color: "#1B2232", fontWeight: 500 }}>
          {startDate ? dayjs(startDate).format("DD MMM YYYY") : "-"} -{" "}
          {endDate ? dayjs(endDate).format("DD MMM YYYY") : "-"}
        </span>
      </Typography>

      {/* Table Section */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #DFE5EC",
          borderRadius: "12px",
          overflow: "hidden",
          mb: 2,
          position: "relative",
        }}
      >
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.6)",
              zIndex: 1,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        )}
        {activeTab === 3 ? (
          <AppTable
            columns={agentActivityColumns}
            rows={getAgentActivityPageData()}
            rowKey={(row, index) => index}
            maxHeight={500}
            hideMainScroll={false}
            containerSx={{ minWidth: 1200 }}
            rowSx={analyticsRowSx}
            paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
          />
        ) : (
          <AppTable
            columns={chatAnalyticsColumns}
            rows={analyticsData}
            rowKey={(row, index) => index}
            maxHeight={500}
            hideMainScroll={false}
            containerSx={{ minWidth: 1200 }}
            rowSx={analyticsRowSx}
            paperSx={{ border: "none", boxShadow: "none", mb: 0 }}
          />
        )}
      </Paper>
    </Container>
  );
};

export default ChatAnalytics;